<template>
  <UPage>
    <UPageHeader
      :title="(projectDetails.fldBez as string) || 'Projektdetails'"
      :description="`Nummer: ${fldPrjNr}`"
    >
      <template #links>
        <UButton
          icon="i-heroicons-arrow-left"
          label="Zurück zu Projekten"
          variant="ghost"
          @click="$router.push('/microtech-hub/projects')"
        />
      </template>
    </UPageHeader>

    <UPageBody>
      <UTabs v-model="activeTab" :items="tabs" class="w-full">
        <template #details>
          <UCard>
            <template #header>
              <div class="flex items-center justify-between">
                <h3 class="text-base font-semibold">Stammdaten</h3>
                <UButton
                  color="neutral"
                  icon="i-heroicons-cog-6-tooth"
                  label="Felder anpassen"
                  size="xs"
                  variant="ghost"
                  @click="openColumnModal('details')"
                />
              </div>
            </template>
            <div v-if="loading" class="flex justify-center py-10">
              <UIcon class="w-10 h-10 animate-spin text-muted" name="i-heroicons-arrow-path" />
            </div>
            <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-4">
              <div
                v-for="field in selectedColumns"
                :key="field.key"
                class="border-b border-default pb-2"
              >
                <p class="text-xs font-medium text-muted uppercase tracking-wider">
                  {{ field.label }}
                </p>
                <p class="mt-1 text-sm text-highlighted">
                  {{ getValue(projectDetails, field.key) || '-' }}
                </p>
              </div>
            </div>
          </UCard>
        </template>

        <template #transactions>
          <PortalTable
            ref="transactionsTable"
            :columns-loading="fetchingSchema"
            :data="relatedData.transactions"
            :loading="loadingRelated.transactions"
            :selected-columns="selectedTabColumns('transactions')"
            :all-columns="availableColumns('transactions')"
            :total="relatedData.transactions.length"
            :sort-storage-key="TRANS_SORT_STORAGE_KEY"
            class="w-full max-h-[60vh]"
            @select="(row) => navigateToDetail(row.original, 'transactions')"
            @update:columns="onSaveColumns"
            @reset:columns="resetToDefault"
          >
            <template #header>
              <div class="flex items-center justify-between p-4 border-b border-default">
                <h3 class="text-base font-semibold">Vorgänge</h3>
                <UButton
                  color="neutral"
                  icon="i-heroicons-cog-6-tooth"
                  label="Felder anpassen"
                  size="xs"
                  variant="ghost"
                  @click="openColumnModal('transactions')"
                />
              </div>
            </template>
            <template #body-bottom>
              <div
                v-if="hasNextPageByTab.transactions"
                v-intersection-observer="() => fetchRelatedConRead('transactions', true)"
                class="h-1 w-full"
              />
            </template>
          </PortalTable>
        </template>

        <template #transactionsarchive>
          <PortalTable
            ref="transactionsArchiveTable"
            :columns-loading="fetchingSchema"
            :data="relatedData.transactionsarchive"
            :loading="loadingRelated.transactionsarchive"
            :selected-columns="selectedTabColumns('transactionsarchive')"
            :all-columns="availableColumns('transactionsarchive')"
            :total="relatedData.transactionsarchive.length"
            :sort-storage-key="TRANS_ARCHIVE_SORT_STORAGE_KEY"
            class="w-full max-h-[60vh]"
            @select="(row) => navigateToDetail(row.original, 'transactionsarchive')"
            @update:columns="onSaveColumns"
            @reset:columns="resetToDefault"
          >
            <template #header>
              <div class="flex items-center justify-between p-4 border-b border-default">
                <h3 class="text-base font-semibold">Vorgänge Archiv</h3>
                <UButton
                  color="neutral"
                  icon="i-heroicons-cog-6-tooth"
                  label="Felder anpassen"
                  size="xs"
                  variant="ghost"
                  @click="openColumnModal('transactionsarchive')"
                />
              </div>
            </template>
            <template #body-bottom>
              <div
                v-if="hasNextPageByTab.transactionsarchive"
                v-intersection-observer="() => fetchRelatedConRead('transactionsarchive', true)"
                class="h-1 w-full"
              />
            </template>
          </PortalTable>
        </template>

        <template #dokumente>
          <PortalTable
            ref="dokumenteTable"
            :columns-loading="fetchingSchema"
            :data="relatedData.dokumente"
            :loading="loadingRelated.dokumente"
            :selected-columns="selectedTabColumns('dokumente')"
            :all-columns="availableColumns('dokumente')"
            :total="relatedData.dokumente.length"
            :sort-storage-key="DOC_SORT_STORAGE_KEY"
            class="w-full max-h-[60vh]"
            highlight-row-key="fldGUID"
            :highlight-row-value="($route.query.highlightGUID as string)"
            @select="(row) => downloadDocument(row.original)"
            @update:columns="onSaveColumns"
            @reset:columns="resetToDefault"
          >
            <template #header>
              <div class="flex items-center justify-between p-4 border-b border-default">
                <h3 class="text-base font-semibold">Dokumente</h3>
                <UButton
                  color="neutral"
                  icon="i-heroicons-cog-6-tooth"
                  label="Felder anpassen"
                  size="xs"
                  variant="ghost"
                  @click="openColumnModal('dokumente')"
                />
              </div>
            </template>
            <template #body-bottom>
              <div
                v-if="hasNextPageByTab.dokumente"
                v-intersection-observer="() => fetchDocuments(true)"
                class="h-1 w-full"
              />
            </template>
          </PortalTable>
        </template>
      </UTabs>

      <ColumnSelectionModal
        ref="columnModal"
        :all-columns="allPossibleColumns"
        :initial-selected="selectedTabColumnsMapped"
        :loading="fetchingMetadata || fetchingSchema"
        :title="modalTitle"
        :description="modalDescription"
        @save="onSaveColumns"
        @reset="resetToDefault"
      />
    </UPageBody>
  </UPage>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useRouter } from 'vue-router'
import axios from 'axios'
import PortalTable from '@/components/PortalTable.vue'
import ColumnSelectionModal from '@/components/ColumnSelectionModal.vue'
import { useProjectStore } from '@/stores/project'
import { fetchRelationFields, fetchTableFields } from '@/utils/graphql-introspection'
import { useAuthStore } from '@/stores/auth'
import { useDocumentDownload } from '@/composables/useDocumentDownload'

const authStore = useAuthStore()
const route = useRoute()
const projectStore = useProjectStore()

const customerNo = computed(
  () => props.customerNo || authStore.userInfo?.activeTenant?.customerNo || '',
)
const tenantName = computed(
  () => props.tenantName || authStore.userInfo?.activeTenant?.tenantName || '',
)

const props = defineProps<{
  customerNo: string
  tenantName: string
  fldPrjNr: string
  isArchive?: string
}>()

const loading = ref(true)
const fetchingMetadata = ref(false)
const fetchingSchema = ref(false)
const columnModal = ref()
const projectDetails = ref<Record<string, unknown>>({})
const allPossibleColumns = ref<
  Array<{ key: string; label: string; graphql?: string; locked?: boolean }>
>([])
const selectedColumns = ref<
  Array<{ key: string; label: string; locked?: boolean; graphql?: string }>
>([])

const activeTab = ref(route.query.tab as string || 'details')
watch(
  () => route.query.tab,
  (newTab) => {
    if (newTab && typeof newTab === 'string') {
      activeTab.value = newTab
    } else {
      activeTab.value = 'details'
    }
  },
)
const tabs = [
  { label: 'Details', slot: 'details', value: 'details' },
  { label: 'Vorgänge', slot: 'transactions', value: 'transactions' },
  { label: 'Vorgänge Archiv', slot: 'transactionsarchive', value: 'transactionsarchive' },
  { label: 'Dokumente', slot: 'dokumente', value: 'dokumente' },
]

const loadingRelated = ref<Record<string, boolean>>({
  transactions: false,
  transactionsarchive: false,
  dokumente: false,
})
const relatedData = ref<Record<string, Record<string, unknown>[]>>({
  transactions: [],
  transactionsarchive: [],
  dokumente: [],
})
const endCursorByTab = ref<Record<string, string>>({
  transactions: '',
  transactionsarchive: '',
  dokumente: '',
})
const hasNextPageByTab = ref<Record<string, boolean>>({
  transactions: false,
  transactionsarchive: false,
  dokumente: false,
})
const limit = ref(100)

const availableColumnsByTab = ref<
  Record<string, Array<{ key: string; label: string; graphql?: string; name?: string }>>
>({
  details: [],
  transactions: [],
  transactionsarchive: [],
  dokumente: [],
})
const selectedColumnsByTab = ref<
  Record<
    string,
    Array<{ key: string; label: string; graphql?: string; name?: string; locked?: boolean }>
  >
>({
  details: [],
  transactions: [],
  transactionsarchive: [],
  dokumente: [],
})

const STORAGE_KEY = computed(
  () =>
    `cc_prj_det_cols_${customerNo.value}_${tenantName.value}_${props.isArchive === 'true' ? 'archive' : 'open'}`,
)
const TRANS_STORAGE_KEY = computed(
  () => `cc_prj_det_trans_cols_${customerNo.value}_${tenantName.value}`,
)
const TRANS_ARCHIVE_STORAGE_KEY = computed(
  () => `cc_prj_det_trans_arch_cols_${customerNo.value}_${tenantName.value}`,
)
const DOC_STORAGE_KEY = computed(
  () => `cc_prj_det_doc_cols_${customerNo.value}_${tenantName.value}`,
)
const TRANS_SORT_STORAGE_KEY = computed(
  () => `cc_prj_det_trans_sort_${customerNo.value}_${tenantName.value}`,
)
const TRANS_ARCHIVE_SORT_STORAGE_KEY = computed(
  () => `cc_prj_det_trans_arch_sort_${customerNo.value}_${tenantName.value}`,
)
const DOC_SORT_STORAGE_KEY = computed(
  () => `cc_prj_det_doc_sort_${customerNo.value}_${tenantName.value}`,
)

const activeTabKey = computed(() => activeTab.value)
const modalTitle = computed(
  () => `Spalten anpassen für ${tabs.find((t) => t.value === activeTabKey.value)?.label || ''}`,
)
const modalDescription = ref('Passen Sie die Anzeige der Spalten für die aktuelle Tab-Ansicht an.')

const selectedTabColumnsMapped = computed(() => {
  const fields = selectedColumnsByTab.value[activeTabKey.value] || []
  return fields.map((f) => ({
    key: f.key || f.name || '',
    label: f.label || f.name || '',
    locked: f.locked,
    graphql: f.graphql,
  }))
})

function selectedTabColumns(tab: string) {
  const fields = selectedColumnsByTab.value[tab] || []
  return fields.map((f) => ({
    key: f.key || f.name || '',
    label: f.label || f.name || '',
    locked: f.locked,
    graphql: f.graphql,
  }))
}

function availableColumns(tab: string) {
  const fields = availableColumnsByTab.value[tab] || []
  return fields.map((f) => ({
    key: f.key || f.name || '',
    label: f.label || f.name || '',
    graphql: f.graphql,
  }))
}

function loadSavedColumns() {
  const saved = projectStore.loadColumns(STORAGE_KEY.value)
  if (saved) {
    const parsed = [...saved]
    const updated = parsed.map((p) => {
      const found = availableColumnsByTab.value.details.find((c) => c.key === p.key)
      if (found) {
        return { ...p, label: found.label, graphql: found.graphql }
      }
      return p
    })

    if (!updated.find((c: { key: string }) => c.key === 'fldPrjNr')) {
      updated.unshift({ key: 'fldPrjNr', label: 'Projektnummer', locked: true })
    } else {
      const idx = updated.findIndex((c: { key: string }) => c.key === 'fldPrjNr')
      updated[idx].locked = true
    }
    selectedColumns.value = updated
    selectedColumnsByTab.value.details = updated
  } else {
    setDefaultColumns()
  }

  const savedTrans = projectStore.loadColumns(TRANS_STORAGE_KEY.value)
  if (savedTrans) {
    selectedColumnsByTab.value.transactions = savedTrans
  } else {
    setDefaultTransColumns()
  }

  const savedTransArch = projectStore.loadColumns(TRANS_ARCHIVE_STORAGE_KEY.value)
  if (savedTransArch) {
    selectedColumnsByTab.value.transactionsarchive = savedTransArch
  } else {
    setDefaultTransArchiveColumns()
  }

  const savedDocs = projectStore.loadColumns(DOC_STORAGE_KEY.value)
  if (savedDocs) {
    selectedColumnsByTab.value.dokumente = savedDocs
  } else {
    setDefaultDocColumns()
  }
}

function setDefaultColumns() {
  const defaults = [
    { key: 'fldPrjNr', label: 'Projektnummer', locked: true },
    { key: 'fldSuchBeg', label: 'Suchbegriff' },
    { key: 'fldBez', label: 'Bezeichnung' },
    { key: 'fldArt', label: 'Projektart' },
    { key: 'fldPrio', label: 'Priorität' },
    { key: 'fldPrjSts', label: 'Projektstatus' },
    { key: 'fldErledigtKz', label: 'Erledigt' },
  ]

  const updatedDefaults = defaults.map((d) => {
    const found = availableColumnsByTab.value.details.find((c) => c.key === d.key)
    if (found) {
      return {
        ...d,
        label: d.label === 'Erledigt' ? 'Erledigt-Kennzeichen' : found.label,
        graphql: found.graphql,
      }
    }
    return d
  })

  selectedColumns.value = updatedDefaults
  selectedColumnsByTab.value.details = updatedDefaults
  projectStore.setColumns(STORAGE_KEY.value, updatedDefaults, false)
}

function setDefaultTransColumns() {
  const defaults = [
    { key: 'fldBelegNr', label: 'Belegnummer', graphql: 'fldBelegNr' },
    { key: 'fldArt', label: 'Art', graphql: 'fldArt' },
    { key: 'fldSuchBeg', label: 'Suchbegriff', graphql: 'fldSuchBeg' },
    { key: 'fldDat', label: 'Datum', graphql: 'fldDat' },
  ]
  selectedColumnsByTab.value.transactions = defaults
  projectStore.setColumns(TRANS_STORAGE_KEY.value, defaults, false)
}

function setDefaultTransArchiveColumns() {
  const defaults = [
    { key: 'fldBelegNr', label: 'Belegnummer', graphql: 'fldBelegNr' },
    { key: 'fldArt', label: 'Art', graphql: 'fldArt' },
    { key: 'fldSuchBeg', label: 'Suchbegriff', graphql: 'fldSuchBeg' },
    { key: 'fldDat', label: 'Datum', graphql: 'fldDat' },
  ]
  selectedColumnsByTab.value.transactionsarchive = defaults
  projectStore.setColumns(TRANS_ARCHIVE_STORAGE_KEY.value, defaults, false)
}

function setDefaultDocColumns() {
  const defaults = [
    { key: 'fldNa', label: 'Name', graphql: 'fldNa' },
    { key: 'fldExt', label: 'Erweiterung', graphql: 'fldExt' },
  ]
  selectedColumnsByTab.value.dokumente = defaults
  projectStore.setColumns(DOC_STORAGE_KEY.value, defaults, false)
}

function resetToDefault() {
  if (activeTabKey.value === 'details') {
    projectStore.removeColumns(STORAGE_KEY.value)
    setDefaultColumns()
  } else if (activeTabKey.value === 'transactions') {
    projectStore.removeColumns(TRANS_STORAGE_KEY.value)
    setDefaultTransColumns()
  } else if (activeTabKey.value === 'transactionsarchive') {
    projectStore.removeColumns(TRANS_ARCHIVE_STORAGE_KEY.value)
    setDefaultTransArchiveColumns()
  } else {
    projectStore.removeColumns(DOC_STORAGE_KEY.value)
    setDefaultDocColumns()
  }
}

async function fetchMetadata() {
  if (!customerNo.value || !tenantName.value) return
  fetchingMetadata.value = true
  try {
    const rootField = props.isArchive === 'true' ? 'tblProjectsArchive' : 'tblProjects'
    const fields = await fetchTableFields(rootField, customerNo.value, tenantName.value).catch(
      () => [],
    )
    const mapped = fields
      .filter((f) => !f.name.startsWith('_') && !f.isRelation)
      .map((f) => ({ key: f.name, label: f.label || f.description || f.name, graphql: f.graphql }))

    availableColumnsByTab.value.details = mapped
    allPossibleColumns.value = mapped

    loadSavedColumns()
  } finally {
    fetchingMetadata.value = false
  }
}

async function fetchSchemaForTab(tabKey: string) {
  if (tabKey === 'details') return
  if (!customerNo.value || !tenantName.value) return
  fetchingSchema.value = true
  try {
    let typeName = ''
    if (tabKey === 'dokumente') typeName = 'DocumentRowQueryRead'
    else if (tabKey === 'transactions' || tabKey === 'transactionsarchive')
      typeName = 'TransactionRowQueryRead'

    const fields = await fetchRelationFields(typeName, customerNo.value, tenantName.value)
    availableColumnsByTab.value[tabKey] = fields
      .filter((f) => !f.name.startsWith('_') && !f.name.endsWith('_int'))
      .map((f) => ({
        key: f.name,
        label: f.label || f.description || f.name,
        graphql: f.graphql || f.name,
      }))

    if (activeTabKey.value === tabKey) {
      allPossibleColumns.value = availableColumnsByTab.value[tabKey]
    }
  } finally {
    fetchingSchema.value = false
  }
}

async function openColumnModal(tabKey: string) {
  if (
    ['dokumente', 'transactions', 'transactionsarchive'].includes(tabKey) &&
    availableColumnsByTab.value[tabKey].length === 0
  ) {
    await fetchSchemaForTab(tabKey)
  }
  allPossibleColumns.value = availableColumnsByTab.value[tabKey]
  columnModal.value?.open()
}

function onSaveColumns(newCols: Array<{ key: string; label: string }>) {
  const key = activeTabKey.value
  const available = availableColumnsByTab.value[key]
  const mapped = newCols.map((nc) => {
    const found = available.find((a) => (a.key || a.name) === nc.key)
    return { ...nc, graphql: found?.graphql }
  })

  selectedColumnsByTab.value[key] = mapped
  if (key === 'details') {
    selectedColumns.value = mapped
    projectStore.setColumns(STORAGE_KEY.value, mapped)
  } else if (key === 'transactions') {
    projectStore.setColumns(TRANS_STORAGE_KEY.value, mapped)
  } else if (key === 'transactionsarchive') {
    projectStore.setColumns(TRANS_ARCHIVE_STORAGE_KEY.value, mapped)
  } else {
    projectStore.setColumns(DOC_STORAGE_KEY.value, mapped)
  }
}

const router = useRouter()

function navigateToDetail(row: Record<string, unknown>, tabKey: string) {
  if (tabKey.startsWith('transactions')) {
    const belegNr = row.fldBelegNr
    if (belegNr) {
      router.push({
        path: `/microtech-hub/transactions/${belegNr}`,
        query: { isArchive: tabKey === 'transactionsarchive' ? 'true' : undefined },
      })
    }
  }
}

async function fetchProjectDetails() {
  loading.value = true
  try {
    const selectionSet = buildSelectionSet(selectedColumns.value)
    const rootField = props.isArchive === 'true' ? 'tblProjectsArchive' : 'tblProjects'
    const query = `
      query {
        ${rootField} {
          rowRead(kf1PrjNr: { string: "${props.fldPrjNr}" }) {
            ${selectionSet}
          }
        }
      }
    `
    const response = await axios.post('/api/microtech/v1/proxy/graphql', { query })
    projectDetails.value = response.data?.data?.[rootField]?.rowRead || {}
  } catch (error) {
    console.error('Error fetching project details:', error)
  } finally {
    loading.value = false
  }
}

async function fetchRelatedConRead(tabKey: string, loadMore = false) {
  if (loadMore && !hasNextPageByTab.value[tabKey]) return
  if (loadingRelated.value[tabKey]) return

  loadingRelated.value[tabKey] = true
  const selectionSet = buildSelectionSet(selectedColumnsByTab.value[tabKey])
  const afterParam =
    loadMore && endCursorByTab.value[tabKey] ? `, after: "${endCursorByTab.value[tabKey]}"` : ''

  let rootField = ''
  let filter = ''

  if (tabKey === 'dokumente') {
    rootField = 'tblDocuments'
    filter = `allBetween: { byNr: { kf1Nr: { string: "${props.fldPrjNr}" } } }
              fastFilter: { eq: [{ field: fldDBBkzBez }, { value: "Prj" }] }`
  } else if (tabKey === 'transactions') {
    rootField = 'tblTransactions'
    filter = `allBetween: { byPrjNr: { kf1PrjNr: { string: "${props.fldPrjNr}" } } }`
  } else if (tabKey === 'transactionsarchive') {
    rootField = 'tblTransactionsArchive'
    filter = `allBetween: { byPrjNr: { kf1PrjNr: { string: "${props.fldPrjNr}" } } }`
  }

  const query = `
    query {
      ${rootField} {
        conRead(
          first: ${limit.value}
          ${afterParam}
          ${filter}
        ) {
          pageInfo {
            endCursor
            hasNextPage
          }
          edges {
            node {
              ${tabKey === 'dokumente' ? 'fldID\nfldExt\nfldGUID' : ''}
              ${selectionSet}
            }
          }
        }
      }
    }
  `

  try {
    const response = await axios.post('/api/microtech/v1/proxy/graphql', { query })
    const conRead = response.data?.data?.[rootField]?.conRead
    if (conRead) {
      const newRows = conRead.edges.map((edge: { node: Record<string, unknown> }) => edge.node)
      if (loadMore) {
        relatedData.value[tabKey] = [...relatedData.value[tabKey], ...newRows]
      } else {
        relatedData.value[tabKey] = newRows
      }
      endCursorByTab.value[tabKey] = conRead.pageInfo.endCursor
      hasNextPageByTab.value[tabKey] = conRead.pageInfo.hasNextPage
    }
  } catch (error) {
    console.error(`Error fetching ${tabKey}:`, error)
  } finally {
    loadingRelated.value[tabKey] = false
  }
}

async function fetchDocuments(loadMore = false) {
  return fetchRelatedConRead('dokumente', loadMore)
}

const { downloadDocument } = useDocumentDownload()

function buildSelectionSet(columns: Array<{ key?: string; name?: string; graphql?: string }>) {
  const customSelections = new Set<string>()
  const tree: Record<string, unknown> = {}

  columns.forEach((col) => {
    const graphql = col.graphql || col.key || col.name || ''
    if (!graphql) return
    if (graphql.includes('{')) {
      customSelections.add(graphql)
    } else {
      const parts = graphql.split('.')
      let current = tree as Record<string, unknown>
      parts.forEach((part) => {
        if (!current[part]) {
          current[part] = {} as Record<string, unknown>
        }
        current = current[part] as Record<string, unknown>
      })
    }
  })

  function stringify(obj: Record<string, unknown>): string {
    return Object.keys(obj)
      .map((k) => {
        const val = obj[k]
        if (val && typeof val === 'object' && Object.keys(val).length > 0) {
          const sub = stringify(val as Record<string, unknown>)
          return `${k} { ${sub} }`
        }
        return k
      })
      .join('\n')
  }

  const baseSelection = stringify(tree)
  return [baseSelection, ...Array.from(customSelections)].filter((s) => s).join('\n')
}

function getValue(obj: Record<string, unknown>, key: string): unknown {
  return key
    .split('.')
    .reduce(
      (o: Record<string, unknown> | unknown, i) =>
        o && typeof o === 'object' && i in o ? (o as Record<string, unknown>)[i] : undefined,
      obj,
    )
}

watch(
  selectedColumnsByTab,
  () => {
    const key = activeTabKey.value
    if (key === 'details') fetchProjectDetails()
    else fetchRelatedConRead(key)
  },
  { deep: true },
)

watch(activeTab, (val) => {
  if (route.query.tab !== (val === 'details' ? undefined : val)) {
    router.push({ query: { ...route.query, tab: val === 'details' ? undefined : val } })
  }

  if (val !== 'details' && relatedData.value[val].length === 0) {
    fetchRelatedConRead(val)
  }
})

onMounted(async () => {
  if (customerNo.value && tenantName.value) {
    await fetchMetadata()
    await fetchProjectDetails()
  }

  watch([customerNo, tenantName], async ([newCust, newTen]) => {
    if (newCust && newTen) {
      await fetchMetadata()
      await fetchProjectDetails()
    }
  })
})
</script>
